import type { ComponentProps } from 'react';
import React, { useState } from 'react';
import styled from '@emotion/styled';
import type { OverflowProps } from 'styled-system';
import { overflow } from 'styled-system';

import { useMatchesViewport } from '../../../../hooks/media';
import { useLocalization } from '../../../../providers/LocaleProvider';
import type { ThemeLeftProps, ThemeRightProps } from '../../../../theme/system-utilities';
import { themeLeft, themeRight } from '../../../../theme/system-utilities';
import { getActiveSlideProps } from '../../../../utils/image';
import ImageCarousel from '../../../image-carousel/ImageCarousel';
import {
  NextControlButton,
  PrevControlButton,
  type ImageCarouselNavigationProps,
} from '../../../image-carousel/ImageCarouselNavigation';
import type { ArticleMedia } from '../../../image-carousel/types';
import Ratio from '../../../Ratio';
import YouTubeVideo from '../../../YouTubeVideo';
import { ImageZoom } from '../../../Zoom';
import { dispatchZoomInOut } from '../../trackings';
import Image from './Image';

const BIG_IMAGE_SIZES = [480, 640, 720, 860];
const IMAGE_ZOOM = 2;
const ARROW_ICON_SIZE = 32;

interface BigImageHorizontalCarouselProps {
  activeItem?: number;
  items: ArticleMedia[];
  updateActiveItem?: (index: number) => void;
  name?: string;
  withFixedSize?: boolean;
  baseId: string;
  productGalleryV2?: boolean;
}

const PrevControl = styled(PrevControlButton)<ThemeLeftProps>`
  ${themeLeft};
`;

const NextControl = styled(NextControlButton)<ThemeRightProps>`
  ${themeRight};
`;

const prevControlStyles: Partial<ComponentProps<typeof PrevControl>> = {
  themeLeft: [null, null, null, '-128px'],
};

const nextControlStyles: Partial<ComponentProps<typeof NextControl>> = {
  themeRight: [null, null, null, '-128px'],
};

const ImageCarouselNavigation = (props: ImageCarouselNavigationProps) => (
  <>
    {!props.hasPrev && <PrevControl {...props} {...prevControlStyles} />}
    {!props.hasNext && <NextControl {...props} {...nextControlStyles} />}
  </>
);

const StyledImageCarousel = styled(ImageCarousel)<OverflowProps>`
  ${overflow};
`;

const BigImageHorizontalCarousel = ({
  activeItem,
  updateActiveItem,
  items,
  name,
  withFixedSize,
  baseId,
  productGalleryV2 = false,
}: BigImageHorizontalCarouselProps) => {
  const { t } = useLocalization();
  const [isZoomIn, setIsZoomIn] = useState(false);
  const {
    matches: { matchesTablet },
  } = useMatchesViewport();

  return (
    <StyledImageCarousel
      baseId={baseId}
      overflow={isZoomIn ? 'visible' : 'hidden'}
      items={items}
      name={name}
      widths={BIG_IMAGE_SIZES}
      activeSlide={activeItem}
      onAfterSlide={activeSlide => {
        updateActiveItem(activeSlide);
        if (activeSlide !== activeItem) {
          setIsZoomIn(false);
        }
      }}
      showPagination={false}
      arrowIconSize={ARROW_ICON_SIZE}
      showNavigation={matchesTablet}
      productGalleryV2={productGalleryV2}
      trackingProps={{ isOverlayOpen: true, productGalleryV2 }}
      renderControls={props => <ImageCarouselNavigation {...props} />}
      renderSlide={slideProps => {
        const { itemIndex, cloneImage, setVideoPlayer } = slideProps;
        const isActive = activeItem === itemIndex;
        let imageVisibility = 1;
        if (isZoomIn) {
          imageVisibility = isActive ? 1 : 0;
        }

        const imageOverflow = isZoomIn && isActive ? 'visible' : 'hidden';

        return (
          <Ratio ratioOpacity={imageVisibility} ratioOverflow={imageOverflow}>
            {cloneImage.ytId ? (
              <YouTubeVideo
                {...getActiveSlideProps(isActive)}
                id={cloneImage.ytId}
                alt={cloneImage.alt || ''}
                widths={1}
                isActive={isActive}
                setVideoPlayer={setVideoPlayer}
                isLazy={!isActive}
              />
            ) : (
              <ImageZoom
                onZoomToggle={zoom => {
                  setIsZoomIn(zoom);
                  dispatchZoomInOut(zoom, isZoomIn);
                }}
                zoom={IMAGE_ZOOM}
                src={cloneImage.path}
                isActive={isActive}
                withKeyboardZoom={activeItem === itemIndex}
              >
                <Image
                  index={itemIndex}
                  src={cloneImage.path}
                  widths={BIG_IMAGE_SIZES}
                  isActive={isActive}
                  alt={cloneImage.alt || ''}
                  withFixedSize={withFixedSize}
                  aria-label={isZoomIn ? t('h24_zoom_out_image') : t('h24_zoom_in_image')}
                />
              </ImageZoom>
            )}
          </Ratio>
        );
      }}
    />
  );
};

export default BigImageHorizontalCarousel;
